import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  Building2,
  Globe,
  MapPin,
  Users,
  Tag,
  TrendingUp,
  Sparkles,
  Download,
  Trash2,
  Eye,
  Zap,
  Target,
  BarChart3,
  Mail,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Clock,
  Flame,
  ThermometerSun,
  Snowflake,
} from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadManagement() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [processingLead, setProcessingLead] = useState(null);
  const [icpData, setIcpData] = useState(null);
  const [generatingICP, setGeneratingICP] = useState(false);

  // New lead form state
  const [newLead, setNewLead] = useState({
    company_name: "",
    website: "",
    industry: "",
    company_size: "",
    location: "",
    description: "",
    linkedin_url: "",
    tags: "",
  });

  // Search form state
  const [searchParams, setSearchParams] = useState({
    industry: "",
    company_size: "",
    location: "",
    keywords: "",
    tech_startup: false,
    looking_for_cmo: false,
  });

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads?limit=100`);
      setLeads(response.data.leads || []);
    } catch (error) {
      toast.error("Failed to fetch leads");
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads, fetchStats]);

  const handleAddLead = async () => {
    if (!newLead.company_name || !newLead.website) {
      toast.error("Company name and website are required");
      return;
    }

    try {
      const tagsArray = newLead.tags
        ? newLead.tags.split(",").map((t) => t.trim()).filter((t) => t)
        : [];

      await axios.post(`${API}/leads`, {
        ...newLead,
        tags: tagsArray,
      });

      toast.success("Lead added successfully");
      setIsAddModalOpen(false);
      setNewLead({
        company_name: "",
        website: "",
        industry: "",
        company_size: "",
        location: "",
        description: "",
        linkedin_url: "",
        tags: "",
      });
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error("Failed to add lead");
    }
  };

  const handleEnrichLead = async (leadId) => {
    setProcessingLead(leadId);
    try {
      await axios.post(`${API}/leads/enrich`, {
        lead_id: leadId,
        enrichment_type: "full",
      });
      toast.success("Enrichment started. This may take a moment...");
      
      // Poll for updates
      setTimeout(() => {
        fetchLeads();
        fetchStats();
        setProcessingLead(null);
      }, 5000);
    } catch (error) {
      toast.error("Failed to start enrichment");
      setProcessingLead(null);
    }
  };

  const handleAnalyzeLead = async (leadId) => {
    setProcessingLead(leadId);
    try {
      const response = await axios.post(`${API}/leads/${leadId}/analyze`);
      toast.success("Revenue Acceleration Score Analysis started!");
      
      // Redirect to job status page
      window.location.href = `/jobs/${response.data.job_id}`;
    } catch (error) {
      toast.error("Failed to start analysis");
      setProcessingLead(null);
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;

    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleScoreLead = async (leadId) => {
    setProcessingLead(leadId);
    try {
      await axios.post(`${API}/leads/${leadId}/score`);
      toast.success("Lead scoring started...");
      
      setTimeout(() => {
        fetchLeads();
        fetchStats();
        setProcessingLead(null);
      }, 3000);
    } catch (error) {
      toast.error("Failed to score lead");
      setProcessingLead(null);
    }
  };

  const handleBulkAnalyze = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Select at least one lead");
      return;
    }

    try {
      await axios.post(`${API}/leads/bulk-analyze`, {
        lead_ids: selectedLeads,
      });
      toast.success(`Started analysis for ${selectedLeads.length} leads`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (error) {
      toast.error("Failed to start bulk analysis");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/leads/export`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "leads_export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Leads exported successfully");
    } catch (error) {
      toast.error("Failed to export leads");
    }
  };

  const handleSearch = async () => {
    try {
      const response = await axios.post(`${API}/leads/search`, searchParams);
      setLeads(response.data.leads || []);
      setIsSearchModalOpen(false);
      toast.success(`Found ${response.data.total} leads`);
    } catch (error) {
      toast.error("Search failed");
    }
  };

  const handleGenerateICP = async () => {
    setGeneratingICP(true);
    try {
      const response = await axios.post(`${API}/icp/generate`, {
        based_on: "high_scores",
      });
      setIcpData(response.data.icp);
      toast.success("ICP generated successfully");
    } catch (error) {
      toast.error("Failed to generate ICP");
    } finally {
      setGeneratingICP(false);
    }
  };

  const getScoreBadge = (lead) => {
    if (!lead.ai_score) {
      return <Badge variant="outline" className="text-gray-500">Not Scored</Badge>;
    }
    if (lead.score_recommendation === "hot") {
      return (
        <Badge className="bg-red-500 text-white">
          <Flame className="w-3 h-3 mr-1" />
          Hot ({lead.ai_score})
        </Badge>
      );
    }
    if (lead.score_recommendation === "warm") {
      return (
        <Badge className="bg-orange-500 text-white">
          <ThermometerSun className="w-3 h-3 mr-1" />
          Warm ({lead.ai_score})
        </Badge>
      );
    }
    return (
      <Badge variant="secondary">
        <Snowflake className="w-3 h-3 mr-1" />
        Cold ({lead.ai_score})
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: "bg-blue-100 text-blue-800", icon: Clock },
      enriching: { color: "bg-yellow-100 text-yellow-800", icon: Loader2 },
      enriched: { color: "bg-green-100 text-green-800", icon: CheckCircle2 },
      analyzing: { color: "bg-purple-100 text-purple-800", icon: Loader2 },
      analyzed: { color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
      contacted: { color: "bg-indigo-100 text-indigo-800", icon: Mail },
      converted: { color: "bg-green-500 text-white", icon: TrendingUp },
      error: { color: "bg-red-100 text-red-800", icon: AlertCircle },
    };
    
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-brand animate-spin mx-auto mb-4" />
          <p className="text-[#6b7280]">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-brand hover:text-brand-dark transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back to Home</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleExport}
                data-testid="export-leads-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-lead-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Lead
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Add New Lead</DialogTitle>
                    <DialogDescription>
                      Add a company to your lead database for enrichment and analysis.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company Name *</Label>
                      <Input
                        id="company_name"
                        placeholder="e.g., Acme Inc"
                        value={newLead.company_name}
                        onChange={(e) =>
                          setNewLead({ ...newLead, company_name: e.target.value })
                        }
                        data-testid="lead-company-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website *</Label>
                      <Input
                        id="website"
                        placeholder="https://example.com"
                        value={newLead.website}
                        onChange={(e) =>
                          setNewLead({ ...newLead, website: e.target.value })
                        }
                        data-testid="lead-website-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="industry">Industry</Label>
                        <Input
                          id="industry"
                          placeholder="e.g., SaaS"
                          value={newLead.industry}
                          onChange={(e) =>
                            setNewLead({ ...newLead, industry: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company_size">Company Size</Label>
                        <Select
                          value={newLead.company_size}
                          onValueChange={(value) =>
                            setNewLead({ ...newLead, company_size: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10</SelectItem>
                            <SelectItem value="11-50">11-50</SelectItem>
                            <SelectItem value="51-200">51-200</SelectItem>
                            <SelectItem value="201-500">201-500</SelectItem>
                            <SelectItem value="501-1000">501-1000</SelectItem>
                            <SelectItem value="1000+">1000+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        placeholder="e.g., San Francisco, CA"
                        value={newLead.location}
                        onChange={(e) =>
                          setNewLead({ ...newLead, location: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tags">Tags (comma separated)</Label>
                      <Input
                        id="tags"
                        placeholder="e.g., tech, startup, saas"
                        value={newLead.tags}
                        onChange={(e) =>
                          setNewLead({ ...newLead, tags: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Brief description of the company"
                        value={newLead.description}
                        onChange={(e) =>
                          setNewLead({ ...newLead, description: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsAddModalOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="btn-primary"
                      onClick={handleAddLead}
                      data-testid="submit-lead-btn"
                    >
                      Add Lead
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#6b7280]">Total Leads</p>
                    <p className="text-3xl font-bold text-brand-ink" data-testid="total-leads-stat">
                      {stats.total_leads}
                    </p>
                  </div>
                  <Building2 className="w-10 h-10 text-brand opacity-20" />
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#6b7280]">Hot Leads</p>
                    <p className="text-3xl font-bold text-red-500" data-testid="hot-leads-stat">
                      {stats.score_distribution?.hot || 0}
                    </p>
                  </div>
                  <Flame className="w-10 h-10 text-red-500 opacity-20" />
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#6b7280]">Warm Leads</p>
                    <p className="text-3xl font-bold text-orange-500" data-testid="warm-leads-stat">
                      {stats.score_distribution?.warm || 0}
                    </p>
                  </div>
                  <ThermometerSun className="w-10 h-10 text-orange-500 opacity-20" />
                </div>
              </CardContent>
            </Card>
            <Card className="card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#6b7280]">Enriched</p>
                    <p className="text-3xl font-bold text-[#10b981]" data-testid="enriched-leads-stat">
                      {stats.status_breakdown?.enriched || 0}
                    </p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-[#10b981] opacity-20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="leads" data-testid="leads-tab">
              <Building2 className="w-4 h-4 mr-2" />
              All Leads
            </TabsTrigger>
            <TabsTrigger value="icp" data-testid="icp-tab">
              <Target className="w-4 h-4 mr-2" />
              ICP Insights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            {/* Action Bar */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Dialog open={isSearchModalOpen} onOpenChange={setIsSearchModalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" data-testid="search-leads-btn">
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Search Leads</DialogTitle>
                      <DialogDescription>
                        Filter leads by criteria
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Industry</Label>
                        <Input
                          placeholder="e.g., Technology"
                          value={searchParams.industry}
                          onChange={(e) =>
                            setSearchParams({
                              ...searchParams,
                              industry: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Company Size</Label>
                        <Input
                          placeholder="e.g., 50-200"
                          value={searchParams.company_size}
                          onChange={(e) =>
                            setSearchParams({
                              ...searchParams,
                              company_size: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input
                          placeholder="e.g., San Francisco"
                          value={searchParams.location}
                          onChange={(e) =>
                            setSearchParams({
                              ...searchParams,
                              location: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Keywords</Label>
                        <Input
                          placeholder="e.g., AI, marketing"
                          value={searchParams.keywords}
                          onChange={(e) =>
                            setSearchParams({
                              ...searchParams,
                              keywords: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={searchParams.tech_startup}
                            onChange={(e) =>
                              setSearchParams({
                                ...searchParams,
                                tech_startup: e.target.checked,
                              })
                            }
                          />
                          Tech Startup
                        </label>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={searchParams.looking_for_cmo}
                            onChange={(e) =>
                              setSearchParams({
                                ...searchParams,
                                looking_for_cmo: e.target.checked,
                              })
                            }
                          />
                          Looking for CMO
                        </label>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => {
                        setSearchParams({
                          industry: "",
                          company_size: "",
                          location: "",
                          keywords: "",
                          tech_startup: false,
                          looking_for_cmo: false,
                        });
                        fetchLeads();
                        setIsSearchModalOpen(false);
                      }}>
                        Clear
                      </Button>
                      <Button className="btn-primary" onClick={handleSearch}>
                        Search
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button
                  variant="outline"
                  onClick={() => {
                    fetchLeads();
                    fetchStats();
                  }}
                  data-testid="refresh-leads-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              {selectedLeads.length > 0 && (
                <Button
                  className="btn-primary"
                  onClick={handleBulkAnalyze}
                  data-testid="bulk-analyze-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Analyze Selected ({selectedLeads.length})
                </Button>
              )}
            </div>

            {/* Leads Table */}
            <Card className="card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={
                          selectedLeads.length === leads.length && leads.length > 0
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeads(leads.map((l) => l.id));
                          } else {
                            setSelectedLeads([]);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Contacts</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">No leads yet</p>
                        <Button
                          className="btn-primary"
                          onClick={() => setIsAddModalOpen(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Your First Lead
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    leads.map((lead) => (
                      <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(lead.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLeads([...selectedLeads, lead.id]);
                              } else {
                                setSelectedLeads(
                                  selectedLeads.filter((id) => id !== lead.id)
                                );
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-brand" />
                            </div>
                            <div>
                              <p className="font-medium text-brand-ink">
                                {lead.company_name}
                              </p>
                              <a
                                href={lead.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-brand hover:underline flex items-center gap-1"
                              >
                                <Globe className="w-3 h-3" />
                                {lead.website?.replace(/https?:\/\//, "").slice(0, 30)}
                              </a>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {lead.industry || <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell>
                          {lead.company_size || <span className="text-gray-400">—</span>}
                        </TableCell>
                        <TableCell>{getStatusBadge(lead.status)}</TableCell>
                        <TableCell>{getScoreBadge(lead)}</TableCell>
                        <TableCell>
                          {lead.contacts?.length > 0 ? (
                            <Badge variant="outline">
                              {lead.contacts.length} contact(s)
                            </Badge>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {processingLead === lead.id ? (
                              <Loader2 className="w-4 h-4 animate-spin text-brand" />
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEnrichLead(lead.id)}
                                  title="Enrich lead data"
                                  data-testid={`enrich-lead-${lead.id}`}
                                >
                                  <Sparkles className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleScoreLead(lead.id)}
                                  title="AI Score"
                                  data-testid={`score-lead-${lead.id}`}
                                >
                                  <BarChart3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleAnalyzeLead(lead.id)}
                                  title="Revenue Acceleration Score Analysis"
                                  data-testid={`analyze-lead-${lead.id}`}
                                >
                                  <Zap className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteLead(lead.id)}
                                  className="text-red-500 hover:text-red-700"
                                  title="Delete"
                                  data-testid={`delete-lead-${lead.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="icp">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-brand" />
                    Generate ICP
                  </CardTitle>
                  <CardDescription>
                    AI-powered Ideal Customer Profile based on your lead data
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="btn-primary w-full"
                    onClick={handleGenerateICP}
                    disabled={generatingICP}
                    data-testid="generate-icp-btn"
                  >
                    {generatingICP ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate ICP from High-Scoring Leads
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {icpData && (
                <Card className="card">
                  <CardHeader>
                    <CardTitle>{icpData.icp_name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-500">Primary Industries</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {icpData.primary_industries?.map((ind, i) => (
                          <Badge key={i} variant="secondary">
                            {ind}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Company Size</Label>
                      <p className="font-medium">{icpData.company_size_range}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Revenue Range</Label>
                      <p className="font-medium">{icpData.revenue_range}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Key Characteristics</Label>
                      <ul className="list-disc list-inside text-sm mt-1">
                        {icpData.key_characteristics?.map((char, i) => (
                          <li key={i}>{char}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Decision Maker Titles</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {icpData.ideal_decision_maker_titles?.map((title, i) => (
                          <Badge key={i} variant="outline">
                            {title}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-600">{icpData.summary}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
